// FileDestinationPicker — "Where to?" for a file, on the register's decision-
// tile idiom: the destination KIND as tiles (a folder here · another site · an
// asset), then the specifics on the selection family (ADR-080): the SITE by
// search (the site lookup's `file_destination` rule — every status, never this
// site, Followed on top), optionally "under an asset of that site" — the ASSET
// by search inside the picked site (the unit lookup's `at_place` rule, cleared and
// SAID when the site changes — the parent declaration), or an asset HERE (the
// same read inside this site); the folder on the picker in the destination's
// vocabulary (the restricted band wearing the lock). Nothing downloads a site
// or a unit list; nothing loads until a field opens. ONE composite for the two
// acts that answer that question: Move (every road) and Import (a folder here ·
// an asset here). Controlled — the value is the whole destination; the caller
// validates.

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { facetKeyFor } from '@ems-app/schemas/files/vocabulary';
import { ChoiceTiles } from '../ui/ChoiceTiles.js';
import { Toggle } from '../ui/Toggle.js';
import { icons } from '../ui/icons.js';
import { assetSource, assetSourceAt } from '../../hooks/useAssets.js';
import { useLookup } from '../../hooks/useLookup.js';
import { codeSource, type OptionRow } from '../../hooks/useOptions.js';
import { siteSource } from '../../hooks/useSites.js';
import { useAssetFace, useSiteFace } from './lookupFaces.js';
import { Picker } from './Picker.js';

export type DestinationRoad = 'folder' | 'site' | 'asset';
export interface FileDestination {
  road: DestinationRoad;
  /** The destination site (the site road), or null for this site. */
  siteId: string | null;
  /** The site road: file it under an asset of that site. */
  underAsset: boolean;
  /** The destination asset (the asset road, or "under an asset"), or null. */
  assetId: string | null;
  /** The folder in the new place (documents). */
  folder: string | null;
}
export const emptyDestination = (road: DestinationRoad, folder: string | null = null): FileDestination => ({ road, siteId: null, underAsset: false, assetId: null, folder });

export interface FileDestinationPickerProps {
  projectId: string;
  /** The site the file sits on — never offered as a destination; "an asset here" looks inside it. */
  siteId: string;
  roads: DestinationRoad[];
  value: FileDestination;
  onChange: (value: FileDestination) => void;
  /** A document asks the folder; media never. */
  document: boolean;
  /** The folder the file is in now — left out of "a folder here". */
  currentFolder?: string | null;
  /** This place's folders; the vocabularies of a site and of an asset. */
  folders: string[];
  /** The class of this place (the "folder here" road) — a site's folders carry a restricted band, an asset's do not. */
  folderClass?: 'site' | 'asset' | 'model';
  siteFolders: string[];
  assetFolders: string[];
  folderLabel: (folder: string) => string;
  errors?: Partial<Record<'site' | 'asset' | 'folder', string>>;
  disabled?: boolean;
  /** Told the destination in the person's words as it is named (a host's toast — the upload's "Uploaded to …"). */
  onWhere?: (where: string) => void;
}

/** The asset a destination looks inside: the picked site's (the site road) or this site's (the asset road). */
const assetSiteOf = (value: FileDestination, siteId: string): string | null => (value.road === 'asset' ? siteId : value.road === 'site' ? value.siteId : null);

export function FileDestinationPicker({ projectId, siteId, roads, value, onChange, document, currentFolder = null, folders, folderClass = 'site', siteFolders, assetFolders, folderLabel, errors = {}, disabled = false, onWhere }: FileDestinationPickerProps) {
  const { t } = useTranslation('files');
  const siteFace = useSiteFace();
  const assetFace = useAssetFace();
  const names = useDestinationNames(projectId, siteId, value);
  const where = destinationLabel(names, value, { folderLabel, document });
  useEffect(() => onWhere?.(where), [where, onWhere]);
  const roadTiles = [
    ...(roads.includes('folder') ? [{ value: 'folder', label: t('move.folderHere'), icon: icons.folder }] : []),
    ...(roads.includes('site') ? [{ value: 'site', label: t('move.otherSite'), icon: icons.site }] : []),
    ...(roads.includes('asset') ? [{ value: 'asset', label: t('move.asset'), icon: icons.asset }] : []),
  ];
  const toAsset = value.road === 'asset' || (value.road === 'site' && value.underAsset);
  const assetSite = assetSiteOf(value, siteId);

  // The sources — the site lookup never offers THIS site; the units of the picked site stand inside it (the parent
  // declaration clears them when the site changes); the units of this site stand on their own.
  const sites = useMemo(() => siteSource(projectId, 'file_destination', siteFace, { site: siteId }), [projectId, siteFace, siteId]);
  const unitsThere = useMemo(() => assetSource(projectId, 'at_place', assetFace, { site: value.siteId ?? '' }), [projectId, assetFace, value.siteId]);
  const unitsHere = useMemo(() => assetSourceAt(projectId, 'at_place', assetFace, { site: siteId }), [projectId, assetFace, siteId]);

  // The folders on offer: the open ones first, then the restricted band, each of its rows wearing the lock (the shared
  // registry says which; only the holder of the facet key sees them at all — the reads already filtered).
  const folderCodes = value.road === 'folder' ? folders.filter((c) => c !== currentFolder) : toAsset ? assetFolders : siteFolders;
  const folderCls = value.road === 'folder' ? folderClass : toAsset ? 'asset' : 'site';
  const folderKey = `${folderCls}:${folderCodes.join(',')}`;
  const folderRows = useMemo<OptionRow[]>(
    () => {
      const rows = folderCodes.map((code) => ({ code, locked: facetKeyFor(folderCls, code) !== null }));
      return [...rows.filter((r) => !r.locked), ...rows.filter((r) => r.locked)].map(({ code, locked }) => ({
        id: code,
        label: folderLabel(code),
        pickable: true,
        group: null,
        ...(locked ? { description: t('move.restrictedFolders'), trailing: <icons.locked size={14} aria-hidden /> } : {}),
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the codes stand in by their joined key
    [folderKey, folderLabel, t],
  );
  const folderSrc = useMemo(() => codeSource(`files:folders:${folderKey}`, folderRows), [folderKey, folderRows]);
  const set = (patch: Partial<FileDestination>) => onChange({ ...value, ...patch });

  return (
    <div className="flex flex-col gap-4">
      {roadTiles.length > 1 && (
        <ChoiceTiles
          ariaLabel={t('move.where')}
          options={roadTiles}
          gridClassName={roadTiles.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}
          value={value.road}
          onChange={(road) => onChange(emptyDestination(road as DestinationRoad, road === 'folder' ? null : value.folder))}
        />
      )}
      {value.road === 'site' && (
        <Picker source={sites} label={t('move.site')} placeholder={t('move.sitePlaceholder')} searchPlaceholder={t('move.sitePlaceholder')} required value={value.siteId} onChange={(v) => set({ siteId: v })} error={errors.site} disabled={disabled} />
      )}
      {value.road === 'site' && document && value.siteId && (
        <Toggle checked={value.underAsset} onChange={(checked) => set({ underAsset: checked, assetId: null })} label={t('move.underAsset')} />
      )}
      {toAsset && assetSite && (
        value.road === 'site' ? (
          <Picker
            source={unitsThere}
            dependsOn={{ value: value.siteId, case: 'must_belong', label: names.site ?? undefined }}
            label={t('move.assetField')}
            placeholder={t('move.assetPlaceholder')}
            searchPlaceholder={t('move.assetPlaceholder')}
            required
            value={value.assetId}
            onChange={(v) => set({ assetId: v })}
            error={errors.asset}
            disabled={disabled}
          />
        ) : (
          <Picker source={unitsHere} label={t('move.assetField')} placeholder={t('move.assetPlaceholder')} searchPlaceholder={t('move.assetPlaceholder')} required value={value.assetId} onChange={(v) => set({ assetId: v })} error={errors.asset} disabled={disabled} />
        )
      )}
      {document && (value.road === 'folder' || value.siteId || value.road === 'asset') && (
        <Picker source={folderSrc} label={t('move.folder')} required placeholder={t('move.folderPick')} value={value.folder} onChange={(v) => set({ folder: v })} error={errors.folder} disabled={disabled} />
      )}
    </div>
  );
}

/** The destination's site and unit NAMED — through the lookups' rows of given ids (the same cache the fields write
 *  to, so a pick is named at once); "…" until named; null when the destination has none. */
export function useDestinationNames(projectId: string, siteId: string, value: FileDestination): { site: string | null; unit: string | null } {
  const siteFace = useSiteFace();
  const assetFace = useAssetFace();
  const assetSite = assetSiteOf(value, siteId);
  const siteIds = useMemo(() => (value.road === 'site' && value.siteId ? [value.siteId] : []), [value.road, value.siteId]);
  const assetIds = useMemo(() => (value.assetId && assetSite ? [value.assetId] : []), [value.assetId, assetSite]);
  const sites = useLookup(projectId, useMemo(() => ({ entity: 'sites' as const, params: { rule: 'file_destination', site: siteId } }), [siteId]), siteIds, { open: false, enabled: siteIds.length > 0 });
  const units = useLookup(projectId, useMemo(() => ({ entity: 'assets' as const, params: { rule: 'at_place', site: assetSite ?? '' } }), [assetSite]), assetIds, { open: false, enabled: assetIds.length > 0 });
  const site = siteIds.length ? (sites.chosenRows[0] ? siteFace(sites.chosenRows[0]).label : '…') : null;
  const unit = assetIds.length ? (units.chosenRows[0] ? (assetFace(units.chosenRows[0]).selectedLabel ?? assetFace(units.chosenRows[0]).label) : '…') : null;
  return { site, unit };
}

/** The destination in the person's words — "RUH-0143 Al Olaya · Teltonika RUT956 · GW-0421 · Permits". */
export function destinationLabel(names: { site: string | null; unit: string | null }, value: FileDestination, opts: { folderLabel: (folder: string) => string; document: boolean }): string {
  return [names.site, names.unit, opts.document && value.folder ? opts.folderLabel(value.folder) : null].filter(Boolean).join(' · ');
}

/** `useDestinationNames` + `destinationLabel` in one — for a host that only wants the words. */
export function useDestinationLabel(projectId: string, siteId: string, value: FileDestination, opts: { folderLabel: (folder: string) => string; document: boolean }): string {
  return destinationLabel(useDestinationNames(projectId, siteId, value), value, opts);
}
